import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Circle, Calendar, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMatchDate } from "@/lib/dates";

const MatchesLandingPage = () => {
  const [liveMatches, setLiveMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [teamsMap, setTeamsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [liveRes, upcomingRes, resultsRes, teamsRes] = await Promise.all([
          axios.get(`${API}/live/matches`),
          // Nearest upcoming fixtures (earliest first)
          axios.get(`${API}/matches?status=scheduled&sort=earliest&limit=8`),
          // Latest results (newest completed first)
          axios.get(`${API}/matches?status=completed&sort=latest&limit=8`),
          axios.get(`${API}/teams`),
        ]);

        const tMap = {};
        teamsRes.data.forEach(t => { tMap[t.team_id] = t; });
        setTeamsMap(tMap);

        setLiveMatches(liveRes.data);
        setUpcomingMatches(upcomingRes.data);
        setRecentResults(resultsRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const formatDate = (d) => formatMatchDate(d, { weekday: 'short', month: 'short', day: 'numeric' });

  const MatchRow = ({ m, isResult = false, isLive = false }) => (
    <Link to={`/matches/${m.match_id}`} className={`bg-white p-4 border-l-4 ${isLive ? 'border-red-500' : isResult ? 'border-[#009E60]' : 'border-[#FFC72C]'} flex flex-col md:flex-row items-center gap-4 hover:bg-gray-50 transition-colors`} data-testid={`match-${m.match_id}`}>
      <div className="text-sm text-gray-500 w-full md:w-32">
        {isLive ? (
          <span className="flex items-center gap-2 text-red-500 font-bold uppercase text-xs">
            <Circle className="w-2 h-2 fill-current animate-pulse" /> Live {m.minute ? `${m.minute}'` : ''}
          </span>
        ) : (
          <>
            <p className="font-medium">{formatDate(m.match_date)}</p>
            <p className="text-xs">{m.match_time || 'TBD'}</p>
          </>
        )}
      </div>
      <div className="flex items-center gap-4 flex-1 justify-center">
        <div className="flex items-center gap-2 flex-1 justify-end">
          <span className="font-heading font-bold text-right hidden md:inline">{teamsMap[m.home_team_id]?.name || 'Home'}</span>
          <span className="font-heading font-bold md:hidden">{teamsMap[m.home_team_id]?.short_name || 'H'}</span>
          <img src={teamsMap[m.home_team_id]?.logo_url} alt="" className="w-10 h-10 object-contain" />
        </div>
        <div className="font-heading font-bold text-2xl min-w-[80px] text-center">
          {isResult || isLive ? `${m.home_score ?? 0} - ${m.away_score ?? 0}` : 'VS'}
          {m.shootout_home_score != null && m.shootout_away_score != null && (
            <div className="text-[10px] font-semibold text-[#FFC72C] uppercase tracking-widest mt-0.5" data-testid={`shootout-tag-${m.match_id}`}>
              ({m.shootout_home_score}-{m.shootout_away_score} pens)
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-1">
          <img src={teamsMap[m.away_team_id]?.logo_url} alt="" className="w-10 h-10 object-contain" />
          <span className="font-heading font-bold hidden md:inline">{teamsMap[m.away_team_id]?.name || 'Away'}</span>
          <span className="font-heading font-bold md:hidden">{teamsMap[m.away_team_id]?.short_name || 'A'}</span>
        </div>
      </div>
      <div className="text-sm text-gray-500 text-center md:w-32">
        {m.stadium || ''}
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="matches-landing-page">
      {/* Hero */}
      <section className="bg-[#09090b] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <Calendar className="w-16 h-16 text-[#FFC72C] mx-auto mb-6" />
          <h1 className="font-heading text-5xl md:text-6xl font-bold uppercase text-white mb-4">
            Matches
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Live scores, upcoming fixtures, and latest results across every ABFA competition.
          </p>
        </div>
      </section>

      <section className="py-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto space-y-12">
          {/* Live */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Circle className="w-4 h-4 fill-red-500 text-red-500 animate-pulse" />
              <h2 className="font-heading text-2xl font-bold uppercase">Live Now</h2>
              <span className="ml-auto text-sm text-gray-500">{liveMatches.length} match{liveMatches.length === 1 ? '' : 'es'}</span>
            </div>
            {loading ? (
              <Skeleton className="h-24 w-full" />
            ) : liveMatches.length > 0 ? (
              <div className="space-y-3">
                {liveMatches.map(m => <MatchRow key={m.match_id} m={m} isLive />)}
              </div>
            ) : (
              <div className="bg-white p-8 text-center text-gray-500 border border-gray-200">
                No matches currently in play.
              </div>
            )}
          </div>

          {/* Upcoming */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold uppercase">Upcoming Fixtures</h2>
              <Link to="/fixtures" className="text-sm text-[#009E60] hover:underline flex items-center gap-1">
                All Fixtures <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
              </div>
            ) : upcomingMatches.length > 0 ? (
              <div className="space-y-3">
                {upcomingMatches.map(m => <MatchRow key={m.match_id} m={m} />)}
              </div>
            ) : (
              <div className="bg-white p-8 text-center text-gray-500 border border-gray-200">
                No upcoming fixtures scheduled.
              </div>
            )}
          </div>

          {/* Recent Results */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold uppercase">Latest Results</h2>
              <Link to="/fixtures" className="text-sm text-[#009E60] hover:underline flex items-center gap-1">
                All Results <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
              </div>
            ) : recentResults.length > 0 ? (
              <div className="space-y-3">
                {recentResults.map(m => <MatchRow key={m.match_id} m={m} isResult />)}
              </div>
            ) : (
              <div className="bg-white p-8 text-center text-gray-500 border border-gray-200">
                No results yet this season.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MatchesLandingPage;
